import { useState, useEffect } from 'react';
import { Bell, BellOff, Download, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationManager({ user, primaryColor }) {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [vapidKey, setVapidKey] = useState('');

  useEffect(() => {
    // Fetch VAPID key from backend
    fetchVapidKey();

    // Check if running as installed PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check notification permission
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }

    // Check if already subscribed
    checkSubscription();

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show install prompt after a short delay if not already installed
      if (!standalone && !localStorage.getItem('installPromptDismissed')) {
        setTimeout(() => setShowInstallPrompt(true), 2000);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS - show custom install instructions
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && !standalone && !localStorage.getItem('installPromptDismissed')) {
      setTimeout(() => setShowInstallPrompt(true), 2000);
    }

    // Show notification banner after 3 seconds if not subscribed
    const timer = setTimeout(() => {
      if ('Notification' in window && Notification.permission === 'default' && !localStorage.getItem('notificationBannerDismissed')) {
        setShowBanner(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      clearTimeout(timer);
    };
  }, []);

  const fetchVapidKey = async () => {
    try {
      const response = await axios.get(`${API}/vapid-public-key`);
      setVapidKey(response.data.public_key);
    } catch (error) {
      console.error('Error fetching VAPID key:', error);
    }
  };

  const checkSubscription = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }
  };

  const subscribeToPush = async () => {
    try {
      // Request permission
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        return false;
      }

      if (!vapidKey) {
        console.error('VAPID key not available');
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to backend
      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/push-subscription`, {
          subscription: subscription.toJSON(),
          user_id: user?.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setIsSubscribed(true);
      setShowBanner(false);
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return false;
    }
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('Install prompt outcome:', outcome);
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('notificationBannerDismissed', 'true');
  };

  // Check if on mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Show install prompt on mobile if not installed and not dismissed
  const shouldShowInstall = isMobile && !isStandalone && !localStorage.getItem('installPromptDismissed');

  return (
    <>
      {/* Install PWA Prompt - Shows on mobile */}
      {(showInstallPrompt || shouldShowInstall) && !isStandalone && (
        <div className="fixed bottom-20 left-4 right-4 max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-5 z-50 border-2"
          style={{ borderColor: primaryColor }}>
          <button 
            onClick={() => {
              setShowInstallPrompt(false);
              localStorage.setItem('installPromptDismissed', 'true');
            }}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: primaryColor }}>
              <span className="text-2xl">🥩</span>
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 text-lg">📲 Installa l'App</h4>
              <p className="text-sm text-gray-600 mt-1">
                Accesso rapido, notifiche ordini e funziona anche offline!
              </p>
              
              {/* Check if iOS */}
              {isIOS ? (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
                  <p className="font-semibold text-amber-800 mb-2">📱 Su iPhone/iPad:</p>
                  <ol className="text-amber-700 space-y-2">
                    <li className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-blue-500 text-white text-xs rounded flex items-center justify-center">1</span>
                      Tocca il pulsante <strong>Condividi</strong> ⬆️ in basso
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-blue-500 text-white text-xs rounded flex items-center justify-center">2</span>
                      Scorri e tocca <strong>"Aggiungi a Home"</strong>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-blue-500 text-white text-xs rounded flex items-center justify-center">3</span>
                      Tocca <strong>"Aggiungi"</strong> in alto
                    </li>
                  </ol>
                </div>
              ) : deferredPrompt ? (
                <button
                  onClick={handleInstallClick}
                  className="mt-3 flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Download size={18} />
                  Installa Gratis
                </button>
              ) : (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl text-sm">
                  <p className="font-semibold text-green-800 mb-2">📱 Su Android:</p>
                  <ol className="text-green-700 space-y-2">
                    <li className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-green-500 text-white text-xs rounded flex items-center justify-center">1</span>
                      Tocca i <strong>3 puntini</strong> ⋮ in alto a destra
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-6 h-6 bg-green-500 text-white text-xs rounded flex items-center justify-center">2</span>
                      Tocca <strong>"Aggiungi a schermata Home"</strong>
                    </li>
                  </ol>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification Permission Banner - More prominent */}
      {showBanner && permission === 'default' && (
        <div className="fixed bottom-4 left-4 right-4 max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-5 z-50 border-2 animate-bounce-once"
          style={{ borderColor: primaryColor }}>
          <button 
            onClick={dismissBanner}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}15` }}>
              <Bell className="w-7 h-7" style={{ color: primaryColor }} />
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 text-lg">🔔 Attiva le Notifiche!</h4>
              <p className="text-sm text-gray-600 mt-1">
                Ricevi un avviso <strong>quando il tuo ordine è pronto</strong> per il ritiro, anche con l'app chiusa!
              </p>
              <button
                onClick={subscribeToPush}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-white font-semibold"
                style={{ backgroundColor: primaryColor }}
              >
                <Bell size={18} />
                Attiva Notifiche
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Small toggle for already dismissed users */}
      {!showBanner && permission === 'default' && !isSubscribed && (
        <button
          onClick={() => setShowBanner(true)}
          className="fixed bottom-4 right-4 w-12 h-12 rounded-full shadow-lg flex items-center justify-center z-40 animate-pulse"
          style={{ backgroundColor: primaryColor }}
          title="Attiva notifiche"
        >
          <BellOff className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Success indicator when subscribed */}
      {isSubscribed && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-full shadow-lg text-sm font-medium z-40 flex items-center gap-2">
          <Bell size={16} />
          Notifiche attive
        </div>
      )}
    </>
  );
}

// Export a simpler component for the settings page
export function NotificationToggle({ primaryColor }) {
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [vapidKey, setVapidKey] = useState('');

  useEffect(() => {
    fetchVapidKey();
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
    checkSubscription();
  }, []);

  const fetchVapidKey = async () => {
    try {
      const response = await axios.get(`${API}/vapid-public-key`);
      setVapidKey(response.data.public_key);
    } catch (error) {
      console.error('Error fetching VAPID key:', error);
    }
  };

  const checkSubscription = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Error checking subscription:', error);
      }
    }
  };

  const toggleNotifications = async () => {
    if (isSubscribed) {
      // Unsubscribe
      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          
          // Remove from backend
          const token = localStorage.getItem('token');
          if (token) {
            await axios.delete(`${API}/push-subscription`, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
          
          setIsSubscribed(false);
        }
      } catch (error) {
        console.error('Error unsubscribing:', error);
      }
    } else {
      // Subscribe
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result === 'granted' && vapidKey) {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey)
          });
          
          // Save to backend
          const token = localStorage.getItem('token');
          if (token) {
            await axios.post(`${API}/push-subscription`, {
              subscription: subscription.toJSON()
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          }
          
          setIsSubscribed(true);
        } catch (error) {
          console.error('Error subscribing:', error);
        }
      }
    }
  };

  if (!('Notification' in window)) {
    return (
      <p className="text-sm text-gray-500">
        Le notifiche non sono supportate su questo browser
      </p>
    );
  }

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
      <div className="flex items-center gap-3">
        {isSubscribed ? (
          <Bell className="w-5 h-5" style={{ color: primaryColor }} />
        ) : (
          <BellOff className="w-5 h-5 text-gray-400" />
        )}
        <div>
          <p className="font-medium text-sm">Notifiche Push</p>
          <p className="text-xs text-gray-500">
            {permission === 'denied' 
              ? 'Bloccate nelle impostazioni del browser' 
              : isSubscribed 
                ? 'Riceverai avvisi per i tuoi ordini' 
                : 'Attiva per ricevere aggiornamenti'}
          </p>
        </div>
      </div>
      <button
        onClick={toggleNotifications}
        disabled={permission === 'denied'}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          permission === 'denied' ? 'bg-gray-200 cursor-not-allowed' :
          isSubscribed ? '' : 'bg-gray-300'
        }`}
        style={isSubscribed ? { backgroundColor: primaryColor } : {}}
      >
        <span 
          className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            isSubscribed ? 'translate-x-6' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}
